import {Form, Formik} from 'formik'
import {useState} from 'react'
import * as Yup from 'yup'

import InlineTable from 'src/feature/bite/component/InlineTable'
import {Table} from 'src/feature/bite/component/Table'

import {useIntl} from 'react-intl'
import {getDateAndTime} from 'src/feature/bite/helpers/date'
import {useStore} from 'src/feature/store/hook'
import {useUpdateStore} from '../../context/useMutation'
import {toast} from 'react-toastify'
import StoreContact from './StoreContact'

const StoreDetailOverview = ({loading}: {loading: boolean}) => {
  const intl = useIntl()

  const [editable, setEditable] = useState(false)
  const {detail, detailSet, recentTransactions} = useStore()
  const {mutateAsync, isLoading} = useUpdateStore({storeId: detail?.id || ''})

  const column = [
    [
      // {
      //   title: intl.formatMessage({ id: 'STORE.DETAIL.OVERVIEW.GROUP_ID' }),
      //   key: 'group_id',
      //   dataIndex: 'groupID'
      // },
      // {
      //   title: intl.formatMessage({ id: 'STORE.DETAIL.OVERVIEW.CHAIN_ID' }),
      //   key: 'chain_id',
      //   dataIndex: 'chainID'
      // },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.EXTRA_ID'}),
        key: 'extra_id',
        dataIndex: 'extraID',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.EXTRA_ID_TOOLTIP'}),
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.RS2_MERCHANT_ID'}),
        key: 'rs2_merchant_id',
        dataIndex: 'rS2MerchantID',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.RS2_MERCHANT_ID_TOOLTIP'}),
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.MERCHANT_NAME'}),
        key: 'name',
        dataIndex: 'name',
        edit: 'input',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.MERCHANT_NAME_TOOLTIP'}),
        maxLength: 51,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.MCC'}),
        key: 'mccCode',
        dataIndex: 'mccCode',
        edit: 'input',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.MCC_TOOLTIP'}),
        maxLength: 51,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        edit: 'select',
        valueSelect: true,
        selects: [
          // {value: 0, text: 'Passive'},
          {value: 1, text: 'Active'},
          {value: 2, text: 'Inactive'},
        ],
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.INVOICE_ISSUER'}),
        key: 'invoiceIssuer',
        dataIndex: 'invoiceIssuer',
        // edit: 'select', formName: 'isoid',
        // selects: isoList?.map((item: any) => ({ value: item.id, text: item.name })),
        render: (text: any) => {
          switch (text) {
            // case 0:
            //   return <>no selected</>;
            case 1:
              return <>ISO</>
            case 2:
              return <>HQ</>
            default:
              return <>-</>
          }
        },
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.ISO_NAME'}),
        key: 'isoName',
        dataIndex: 'isoName',
        // edit: 'select', formName: 'isoid',
        // selects: isoList?.map((item: any) => ({ value: item.id, text: item.name })),
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_NAME'}),
        key: 'slipName',
        dataIndex: 'slipName',
        edit: 'input',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_NAME_TOOLTIP'}),
        maxLength: 21,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_SLOGAN'}),
        key: 'slipSlogan',
        dataIndex: 'slipSlogan',
        edit: 'input',
        tooltip: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_SLOGAN_TOOLTIP'}),
        maxLength: 51,
      },
    ],
    [
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.ADDRESS'}),
        key: 'address',
        dataIndex: 'addressLine',
        edit: 'input',
        maxLength: 101,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.ZIP_CODE'}),
        key: 'zip_code',
        dataIndex: 'zipCode',
        edit: 'input',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.COUNTRY'}),
        key: 'country',
        dataIndex: 'countryName',
        edit: 'country',
        formName: 'countryID',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.CITY'}),
        key: 'city',
        dataIndex: 'cityName',
        edit: 'city',
        formName: 'cityID',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.REGION'}),
        key: 'region',
        dataIndex: 'regionName',
        edit: 'region',
        formName: 'regionId',
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.PHONE'}),
        key: 'phone',
        dataIndex: 'phone',
        edit: 'phone',
        maxLength: 21,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.EMAIL'}),
        key: 'email',
        dataIndex: 'email',
        edit: 'input',
        maxLength: 101,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.AGREEMENT_DATE'}),
        key: 'agreement_date',
        dataIndex: 'agreementDate',
        render: (text: string) => <>{getDateAndTime(text)}</>,
      },
    ],
  ]

  const Data = recentTransactions?.dataList || []

  const Columns = [
    {
      key: 'OrderId',
      dataIndex: 'orderId',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.ORDER_ID'}),
    },
    {
      key: 'Date',
      dataIndex: 'date',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.DATE_TIME'}),
      render: (text: string) => <>{getDateAndTime(text)}</>,
    },
    {
      key: 'TerminalId',
      dataIndex: 'posNo',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.TERMINAL_ID'}),
    },
    {
      key: 'Type',
      dataIndex: 'type',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.TYPE'}),
      render: (text: string) => {
        let color = 'text-success'

        switch (text) {
          case 'Cancel':
            color = 'text-error'
            break
          case 'Refund':
            color = 'text-refund'
            break
          case 'Sale':
            color = 'text-success'
            break
          case 'SignUp':
            color = 'text-signup'
            break
          case 'Birthday':
            color = 'text-info'
            break
          default:
            color = 'text-success'
            break
        }

        return <span className={color}>{text}</span>
      },
    },
    {
      key: 'Amount',
      dataIndex: 'paidAmount',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.AMOUNT'}),
      customMonetize: true,
    },
    {
      key: 'Channel',
      dataIndex: 'channel',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.CHANNEL'}),
    },
    {
      key: 'StoreId',
      dataIndex: 'storeNo',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.STORE_ID'}),
    },
    {
      key: 'detailURL',
      dataIndex: 'transactionId',
      title: intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.ACTION'}),
      render: (transactionId: string) => (
        <a href={`/transaction/${transactionId}`}>
          {intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.DETAIL'})}
        </a>
      ),
    },
  ]

  const validationSchema = Yup.object().shape({
    // firstname: Yup.string()
    //   .required(intl.formatMessage({ id: 'GLOBAL.VALIDATION.REQUIRED_FIELD' }))
    //   .max(50, intl.formatMessage({ id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD' }, { length: 50 })),

    name: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    mccCode: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    addressLine: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    zipCode: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(10, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 10})),
    countryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    cityID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    phone: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(20, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 20})),
    email: Yup.string()
      .email(intl.formatMessage({id: 'GLOBAL.VALIDATION.EMAIL'}))
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    agreementDate: Yup.string().required(
      intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})
    ),
    slipName: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(20, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 20})),
    slipSlogan: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
  })

  return (
    <>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-header'>
          <div className='card-title m-0'>
            <div>
              <h3 className='fw-bold mb-2 mt-5'>
                {intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW'})}
              </h3>
              <p className='text-muted fw-normal fs-6'>
                {intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW_TOOLTIP'})}
              </p>
            </div>
          </div>
        </div>

        {detail && (
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              name: detail?.name || '',
              mccCode: detail?.mccCode || '',
              status: detail?.status || 1,
              addressLine: detail?.addressLine || '',
              zipCode: detail?.zipCode || '',
              countryID: detail?.countryID || '',
              cityID: detail?.cityID || '',
              phone: detail?.phone || '',
              email: detail?.email || '',
              // beyond: detail?.beyond || '',
              // isoid: detail?.isoid || '',
              agreementDate: detail?.agreementDate || '',
              slipName: detail?.slipName || '',
              slipSlogan: detail?.slipSlogan || '',
            }}
            onSubmit={async (values) => {
              if (values.slipName.length > 20) {
                toast.error(
                  intl.formatMessage(
                    {id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD_NAME'},
                    {length: 20, field: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_NAME'})}
                  )
                )
                return
              }

              if (values.slipSlogan.length > 50) {
                toast.error(
                  intl.formatMessage(
                    {id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD_NAME'},
                    {
                      length: 50,
                      field: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.SLIP_SLOGAN'}),
                    }
                  )
                )
                return
              }

              const updateStoreDetailResponse = await mutateAsync({values})
              if (updateStoreDetailResponse && updateStoreDetailResponse?.status) {
                detailSet(updateStoreDetailResponse?.detail)
                setEditable(false)
              }
            }}
          >
            {({values, errors, touched, resetForm}) => (
              <Form>
                <div className='card-body p-9'>
                  <InlineTable
                    loading={loading}
                    dataSource={detail}
                    columns={column}
                    editable={editable}
                    values={values}
                  />
                </div>
                <div className='card-footer d-flex w-100 justify-content-end'>
                  {editable ? (
                    <div>
                      <button
                        className='btn btn-primary'
                        style={{marginRight: 10}}
                        onClick={() => {
                          setEditable(false)
                          resetForm()
                        }}
                      >
                        <i className='bi bi-x me-2'></i>
                        {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                      </button>
                      <button className='btn btn-primary' disabled={isLoading}>
                        <i className='bi bi-save2 me-2'></i>
                        {isLoading
                          ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                          : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                      </button>
                    </div>
                  ) : (
                    <button className='btn btn-primary' onClick={() => setEditable(true)}>
                      <i className='bi bi-pencil me-2'></i>
                      {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                    </button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>

      <StoreContact loading={loading} />

      <div className='card mb-5 mb-xl-10'>
        <div className='card-header'>
          <div className='card-title m-0'>
            <h3 className='fw-bold m-0'>
              {intl.formatMessage({id: 'STORE.DETAIL.TRANSACTIONS.RECENT'})}
            </h3>
          </div>
        </div>
        <div className='card-body pt-9 pb-10'>
          <Table loading={loading} dataSource={Data} columns={Columns} />
        </div>
      </div>
    </>
  )
}

export default StoreDetailOverview
